import { FieldDef, GroupDef, GuiDef, ValidatorDef, TabDefinition, CollapsableDef } from '@/objects/gui';
import { ContestantAndRaceContestantTime } from '@/objects/generated';
import { markRaw, computed } from 'vue';
import {
    InputSelect,
    DataSourceLookup,
    InputDateTime,
    DateComp,
    InputNullableInt,
    ValidatedInt,
    InputText,
    ValidatedText,
} from '@/components';
import { useEventCommonStore } from '@/stores';
import { storeToRefs } from 'pinia';
import { type DateType } from '@/utilities/dateutils';
import type { SelectLine, SelectLineValueType } from '@/objects/SelectLine';

export class ContestantAndRaceContestantTimeGuiDef extends GuiDef<ContestantAndRaceContestantTime> {
    constructor() {
        super('ContestantAndRaceContestantTime');

        const EventCommon = storeToRefs(useEventCommonStore());
        const StationsSource = computed(() => {
            return EventCommon.StationsAsSelectLine.value;
        });
        const StationRolesSource = computed(() => {
            return EventCommon.StationRolesAsSelectLine.value;
        });

        const StationID = new FieldDef<ContestantAndRaceContestantTime>();
        StationID.Name = 'StationID';
        StationID.FieldName = 'StationID';
        StationID.DefinedFor = 'Both';
        StationID.TranslationKey = 'contestantandracecontestanttime.stationid';
        StationID.Editable = true;
        StationID.Filterable = true;
        StationID.Sortable = false;
        StationID.CssClasses = '';
        StationID.value = (contestantandracecontestanttime) => contestantandracecontestanttime.StationID;
        StationID.setValue = (contestantandracecontestanttime, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            contestantandracecontestanttime.StationID = value;
        };
        StationID.DetailEditor = markRaw(InputSelect);
        StationID.DetailEditorProps = {
            virtualScrollerMaxItems: 30,
            datasource: { mapsource: StationsSource },
            hideNotfound: true,
            hideCaret: true,
        };
        StationID.CellEditor = markRaw(InputSelect);
        StationID.CellEditorProps = {
            virtualScrollerMaxItems: 30,
            datasource: { mapsource: StationsSource },
            hideNotfound: true,
            hideCaret: true,
            autoOpen: true,
        };
        // @ts-expect-error @ts-ignore
        StationID.CellRenderer = markRaw(DataSourceLookup);
        StationID.CellRendererProps = { datasource: { mapsource: StationsSource }, hideNotfound: true, hideCaret: true };
        StationID.ExportValueFormatter = (obj: ContestantAndRaceContestantTime) => {
            return StationsSource.value.get(obj.StationID)?.text;
        };
        StationID.Validator = null;
        StationID.SortDirection = undefined;
        StationID.cellClassRules = undefined;
        StationID.MinWidth = undefined;
        StationID.MaxWidth = undefined;
        StationID.Tooltip = null;
        this.Fields.push(StationID);

        const StationRoleID = new FieldDef<ContestantAndRaceContestantTime>();
        StationRoleID.Name = 'StationRoleID';
        StationRoleID.FieldName = 'StationRoleID';
        StationRoleID.DefinedFor = 'Both';
        StationRoleID.TranslationKey = 'contestantandracecontestanttime.stationroleid';
        StationRoleID.Editable = true;
        StationRoleID.Filterable = true;
        StationRoleID.Sortable = false;
        StationRoleID.CssClasses = '';
        StationRoleID.value = (contestantandracecontestanttime) => contestantandracecontestanttime.StationRoleID;
        StationRoleID.setValue = (contestantandracecontestanttime, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            contestantandracecontestanttime.StationRoleID = value;
        };
        StationRoleID.DetailEditor = markRaw(InputSelect);
        StationRoleID.DetailEditorProps = {
            virtualScrollerMaxItems: 30,
            datasource: { mapsource: StationRolesSource },
            hideNotfound: true,
            hideCaret: true,
        };
        StationRoleID.CellEditor = markRaw(InputSelect);
        StationRoleID.CellEditorProps = {
            virtualScrollerMaxItems: 30,
            datasource: { mapsource: StationRolesSource },
            hideNotfound: true,
            hideCaret: true,
            autoOpen: true,
        };
        // @ts-expect-error @ts-ignore
        StationRoleID.CellRenderer = markRaw(DataSourceLookup);
        StationRoleID.CellRendererProps = { datasource: { mapsource: StationRolesSource }, hideNotfound: true, hideCaret: true };
        StationRoleID.ExportValueFormatter = (obj: ContestantAndRaceContestantTime) => {
            return StationRolesSource.value.get(obj.StationRoleID)?.text;
        };
        StationRoleID.Validator = null;
        StationRoleID.SortDirection = undefined;
        StationRoleID.cellClassRules = undefined;
        StationRoleID.MinWidth = undefined;
        StationRoleID.MaxWidth = undefined;
        StationRoleID.Tooltip = null;
        this.Fields.push(StationRoleID);

        const Time = new FieldDef<ContestantAndRaceContestantTime>();
        Time.Name = 'Time';
        Time.FieldName = 'Time';
        Time.DefinedFor = 'Both';
        Time.TranslationKey = 'contestantandracecontestanttime.time';
        Time.Editable = true;
        Time.Filterable = false;
        Time.Sortable = false;
        Time.CssClasses = '';
        Time.value = (contestantandracecontestanttime) => contestantandracecontestanttime.Time;
        Time.setValue = (contestantandracecontestanttime, value) => (contestantandracecontestanttime.Time = value);
        Time.DetailEditor = markRaw(InputDateTime);
        Time.DetailEditorProps = { format: 'DATETIMEMS3' as DateType };
        Time.CellEditor = markRaw(InputDateTime);
        Time.CellEditorProps = { format: 'DATETIMEMS3' as DateType };
        Time.CellRenderer = markRaw(DateComp);
        Time.CellRendererProps = { format: 'DATETIMEMS3' as DateType };
        Time.Validator = null;
        Time.SortDirection = undefined;
        Time.cellClassRules = undefined;
        Time.MinWidth = 240;
        Time.MaxWidth = 240;
        Time.Tooltip = null;
        this.Fields.push(Time);

        const LapNumber = new FieldDef<ContestantAndRaceContestantTime>();
        LapNumber.Name = 'LapNumber';
        LapNumber.FieldName = 'LapNumber';
        LapNumber.DefinedFor = 'Both';
        LapNumber.TranslationKey = 'contestantandracecontestanttime.lapnumber';
        LapNumber.Editable = true;
        LapNumber.Filterable = false;
        LapNumber.Sortable = false;
        LapNumber.CssClasses = '';
        LapNumber.value = (contestantandracecontestanttime) => contestantandracecontestanttime.LapNumber;
        LapNumber.setValue = (contestantandracecontestanttime, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            contestantandracecontestanttime.LapNumber = value;
        };
        LapNumber.DetailEditor = markRaw(InputNullableInt);
        LapNumber.DetailEditorProps = {};
        LapNumber.CellEditor = markRaw(InputNullableInt);
        LapNumber.CellEditorProps = {};
        LapNumber.CellRenderer = markRaw(ValidatedInt);
        LapNumber.CellRendererProps = {};
        LapNumber.Validator = null;
        LapNumber.SortDirection = undefined;
        LapNumber.cellClassRules = undefined;
        LapNumber.MinWidth = 80;
        LapNumber.MaxWidth = 80;
        LapNumber.Tooltip = null;
        this.Fields.push(LapNumber);

        const Description = new FieldDef<ContestantAndRaceContestantTime>();
        Description.Name = 'Description';
        Description.FieldName = 'Description';
        Description.DefinedFor = 'Both';
        Description.TranslationKey = 'contestantandracecontestanttime.description';
        Description.Editable = true;
        Description.Filterable = false;
        Description.Sortable = false;
        Description.CssClasses = '';
        Description.value = (contestantandracecontestanttime) => contestantandracecontestanttime.Description;
        Description.setValue = (contestantandracecontestanttime, value) => (contestantandracecontestanttime.Description = value);
        Description.DetailEditor = markRaw(InputText);
        Description.DetailEditorProps = {};
        Description.CellEditor = markRaw(InputText);
        Description.CellEditorProps = {};
        Description.CellRenderer = markRaw(ValidatedText);
        Description.CellRendererProps = {};
        Description.Validator = null;
        Description.SortDirection = undefined;
        Description.cellClassRules = undefined;
        Description.MinWidth = undefined;
        Description.MaxWidth = undefined;
        Description.Tooltip = null;
        this.Fields.push(Description);

        const TimeReason = new FieldDef<ContestantAndRaceContestantTime>();
        TimeReason.Name = 'TimeReason';
        TimeReason.FieldName = 'TimeReason';
        TimeReason.DefinedFor = 'Both';
        TimeReason.TranslationKey = 'contestantandracecontestanttime.timereason';
        TimeReason.Editable = true;
        TimeReason.Filterable = false;
        TimeReason.Sortable = false;
        TimeReason.CssClasses = '';
        TimeReason.value = (contestantandracecontestanttime) => contestantandracecontestanttime.TimeReason;
        TimeReason.setValue = (contestantandracecontestanttime, value) => (contestantandracecontestanttime.TimeReason = value);
        TimeReason.DetailEditor = markRaw(InputText);
        TimeReason.DetailEditorProps = {};
        TimeReason.CellEditor = markRaw(InputText);
        TimeReason.CellEditorProps = {};
        TimeReason.CellRenderer = markRaw(ValidatedText);
        TimeReason.CellRendererProps = {};
        TimeReason.Validator = null;
        TimeReason.SortDirection = undefined;
        TimeReason.cellClassRules = undefined;
        TimeReason.MinWidth = undefined;
        TimeReason.MaxWidth = undefined;
        TimeReason.Tooltip = null;
        this.Fields.push(TimeReason);
    }

    public async LoadDependencies() {
        await useEventCommonStore().EnsureLoaded(['Stations']);
        await useEventCommonStore().EnsureLoaded(['StationRoles']);
    }
}
